import { useState, type FC, useCallback, useEffect, useLayoutEffect } from 'react';

import { useLogin, useAutoLogin } from '../../services/user';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { AUTH_KEY } from '../../constants/storageKeys';

import Logo from '../../assets/cf-logo.png';
import CoverVideo from '../../assets/cover-video.mp4';
import { LoadingScreen } from '../LoadingScreen';

const LoginPage: FC = () => {
  const { doLogin, loading, error } = useLogin();
  const { getItem } = useLocalStorage();
  const { doAutologin, loading: autologinLoading } = useAutoLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [remember, setRemember] = useState(false);

  // const [forgotPassword, setForgotPassword] = useState(false)

  useLayoutEffect(() => {
    const authToken = getItem(AUTH_KEY);
    if (authToken) {
      doAutologin(authToken).catch(err => console.log(err));
    }
  }, []);

  useEffect(() => {
    if (error?.message) {
      setFormError(error.message);
    }
  }, [error]);

  const handleChange = (key: string, value: string) => {
    setFormError('');
    if (key === 'email') {
      setEmail(value);
    } else if (key === 'password') {
      setPassword(value);
    }
  };

  // Solo se recuerda el email; la contraseña nunca se persiste en el navegador.
  const saveCredentials = useCallback(() => {
    localStorage.setItem('factory-email', email);
  }, [email]);

  const clearCredentials = useCallback(() => {
    localStorage.removeItem('factory-email');
  }, []);

  const handleLogin = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (remember) {
      saveCredentials();
    } else {
      clearCredentials();
    }

    doLogin({
      email,
      password
    }).catch((error) => { console.log(error); });
  }, [email, password, remember, saveCredentials, clearCredentials, doLogin]);

  // Cargar el email recordado (nunca la contraseña) al montar
  useEffect(() => {
    const emailStored = localStorage.getItem('factory-email');

    if (emailStored) {
      setEmail(emailStored);
      setRemember(true);
    }
  }, []);

  if (autologinLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex h-screen w-full min-h-screen bg-white dark:bg-gray-900">
      {/* Left side image - hidden on mobile */}
      <div className="hidden md:inline-block md:w-3/4">
        <video
          className="h-full w-full object-cover"
          src={CoverVideo}
          autoPlay
          loop
          muted
          playsInline
        />
      </div>

      {/* Right side form */}
      <div className="w-full md:w-1/4 flex flex-col items-center justify-center bg-gray-50 dark:bg-[#121824] transition-colors md:min-w-[500px]">
        <form onSubmit={handleLogin} className="w-full flex flex-col items-center justify-center px-12 py-8">
          <img src={Logo} alt="CF Logo" className="mb-8 max-w-24 h-24 invert dark:invert-0" />

          <h2 className="text-4xl text-gray-900 dark:text-white font-medium transition-colors">
            Iniciar sesión
          </h2>

          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3 text-center transition-colors">
            ¡Bienvenido de nuevo! Por favor inicia sesión para continuar
          </p>

          {/* Email input */}
          <div className="flex items-center w-full bg-gray-100 dark:bg-white/5 border border-gray-300 dark:border-white/10 h-12 rounded-full overflow-hidden pl-6 gap-2 mt-8 focus-within:border-indigo-500 dark:focus-within:border-indigo-500/50 transition-colors">
            <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0 .55.571 0H15.43l.57.55v9.9l-.571.55H.57L0 10.45zm1.143 1.138V9.9h13.714V1.69l-6.503 4.8h-.697zM13.749 1.1H2.25L8 5.356z"
                className="fill-gray-500 dark:fill-gray-400"
              />
            </svg>
            <input
              type="email"
              placeholder="Correo electrónico"
              value={email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 outline-none text-sm w-full h-full transition-colors"
              required
            />
          </div>

          {/* Password input */}
          <div className="flex items-center mt-6 w-full bg-gray-100 dark:bg-white/5 border border-gray-300 dark:border-white/10 h-12 rounded-full overflow-hidden pl-6 gap-2 focus-within:border-indigo-500 dark:focus-within:border-indigo-500/50 transition-colors">
            <svg width="13" height="17" viewBox="0 0 13 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M13 8.5c0-.938-.729-1.7-1.625-1.7h-.812V4.25C10.563 1.907 8.74 0 6.5 0S2.438 1.907 2.438 4.25V6.8h-.813C.729 6.8 0 7.562 0 8.5v6.8c0 .938.729 1.7 1.625 1.7h9.75c.896 0 1.625-.762 1.625-1.7zM4.063 4.25c0-1.406 1.093-2.55 2.437-2.55s2.438 1.144 2.438 2.55V6.8H4.061z"
                className="fill-gray-500 dark:fill-gray-400"
              />
            </svg>
            <input
              type="password"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => handleChange('password', e.target.value)}
              className="bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 outline-none text-sm w-full h-full transition-colors"
              required
            />
          </div>

          {/* Error message */}
          {formError && (
            <p className="text-red-500 dark:text-red-400 mt-2 transition-colors">
              {formError}
            </p>
          )}

          {/* Remember me and forgot password */}
          <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-0 mt-8 text-gray-600 dark:text-gray-400 transition-colors">
            <div className="flex items-center gap-2">
              <input
                className="h-5 w-5 accent-indigo-500"
                type="checkbox"
                id="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 transition-colors" htmlFor="checkbox">
                Recuérdame
              </label>
            </div>
            <a className="text-sm underline hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors" href="/restore-password">
              ¿Olvidaste tu contraseña?
            </a>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full h-11 rounded-full text-white bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-400 dark:disabled:bg-indigo-700 transition-colors font-medium"
          >
            {loading ? 'Cargando...' : 'Iniciar sesión'}
          </button>
        </form>
      </div>
    </div>
  );
};

export { LoginPage };
